import { ITEMS_BEFORE_PAGE } from "@shared/constants/index";
import Check from "@shared/svg/check.svg?react";
import ChevronDownIcon from "@shared/svg/chevron-down.svg?react";
import ChevronRightIcon from "@shared/svg/chevron-right.svg?react";
import { Avatar } from "@shared/ui/Avatar";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";
import { Input, InputLabel } from "@shared/ui/Inputs";
import Label, { LABEL_SIZES } from "@shared/ui/Label";
import { findIndex } from "@shared/utils/arrayUtils";
import { trimText } from "@shared/utils/textFormatting";
import { WarningCircle, MinusCircle, MagnifyingGlass, X } from "phosphor-react";
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  Fragment,
} from "react";
import { useIntl } from "react-intl";
import { Waypoint } from "react-waypoint";
import useOnClickOutside from "use-onclickoutside";

import DropdownItem from "../DropdownItem";
import { WaypointEventType } from "../SingleSelect/SingleSelectAsyncItem";

export const MULTISELECT_VARIANTS = {
  DEFAULT: "default",
  CHIPS: "chips",
  DUAL_PANE: "dualPane",
} as const;

export type MultiSelectVariantType =
  (typeof MULTISELECT_VARIANTS)[keyof typeof MULTISELECT_VARIANTS];

type AsyncMultiSelectDropdownPropsType = {
  defaultSelectedOptions?: any[];
  setSelectedData?: (data: any[]) => void;
  hideSelectedOptions?: boolean;
  selectedOptions?: any[];
  setSearchQuery?: (value: string) => void;
  onCloseRequest?: (data: any[]) => void;
  allowMulti?: boolean;
  onChange?: (data: any[]) => void;
  onOpen?: (val: boolean) => void;
  label?: string;
  keyId?: string;
  uppercaseLabel?: boolean;
  infoMessage?: string;
  showDropdownClassName?: string;
  showOnlyPlaceHolder?: boolean;
  trimCharCount?: number;
  placeholder?: string;
  dropdownTitle?: string;
  showSearchInput?: boolean;
  searchPlaceholder?: string;
  textAllSelect?: string;
  options?: any[];
  maxOptionLabelWidth?: string;
  showAvatar?: boolean;
  onScrollToBottom?: (event: WaypointEventType) => void;
  isLoading?: boolean;
  customOptionComponent?: (
    option: any,
    onClick: (option: any) => void,
    isSelected: boolean,
  ) => React.JSX.Element;
  showSelectedOptionsPreviewList?: boolean;
  customPreviewItemComponent?: (
    option: any,
    onClick: (option: any) => void,
  ) => React.JSX.Element;
  optionsContainerClassName?: string;
  showRemoveAllButton?: boolean;
  showChipsInline?: boolean;
  customChipComponent?: (
    option: any,
    onClick: (option: any) => void,
  ) => React.JSX.Element;
  variant?: MultiSelectVariantType;
};

const AsyncMultiSelectDropdown = ({
  defaultSelectedOptions = [],
  setSelectedData = () => {},
  hideSelectedOptions = false,
  selectedOptions,
  setSearchQuery = () => {},
  onCloseRequest = () => {},
  allowMulti = true,
  onChange = () => {},
  onOpen = () => {},
  keyId,
  label = "",
  uppercaseLabel = false,
  infoMessage = "",
  showDropdownClassName = "",
  showOnlyPlaceHolder = false,
  trimCharCount = 20,
  placeholder = "",
  dropdownTitle = "",
  showSearchInput = false,
  searchPlaceholder = "",
  textAllSelect = "",
  options = [],
  maxOptionLabelWidth = "",
  showAvatar = false,
  onScrollToBottom = () => {},
  isLoading = false,
  customOptionComponent = null,
  showSelectedOptionsPreviewList = false,
  customPreviewItemComponent = null,
  optionsContainerClassName = "",
  showRemoveAllButton = false,
  showChipsInline = false,
  customChipComponent = null,
  variant = MULTISELECT_VARIANTS.DEFAULT,
}: AsyncMultiSelectDropdownPropsType) => {
  const mounted = useRef();
  const containerRef = useRef();
  const [selectedItems, setSelectedItems] = useState<any[]>(() => {
    if (hideSelectedOptions && selectedOptions?.length) {
      return [...selectedOptions];
    }
    return defaultSelectedOptions?.length ? [...defaultSelectedOptions] : [];
  });
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [showInfoMessage, setShowInfoMessage] = useState<boolean>(false);
  const [hoveredItemValue, setHoveredItemValue] = useState<string | null>(null);
  const { messages } = useIntl();

  const isChipsVariant = variant === MULTISELECT_VARIANTS.CHIPS;
  const effectiveShowChipsInline = isChipsVariant || showChipsInline;
  const effectiveCustomChipComponent = isChipsVariant
    ? (item: any, onRemove: (option: any) => void) => (
        <div className="flex min-w-0 max-w-full rounded-2xl py-2xs px-sm bg-accent text-primary">
          <Label
            size={LABEL_SIZES.SMALL}
            color="text-primary"
            className="flex min-w-0 gap-x-2xs items-center"
          >
            <span className="min-w-0 break-all">{item.chipLabel || item.label}</span>
            <X
              size={14}
              className="cursor-pointer shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(item);
              }}
            />
          </Label>
        </div>
      )
    : customChipComponent;
  useLayoutEffect(() => {
    if (!hideSelectedOptions || !selectedOptions) return;
    setSelectedItems((prev) => {
      if (selectedOptions.length === prev.length) return prev;
      return selectedOptions.length ? [...selectedOptions] : [];
    });
  }, [hideSelectedOptions, selectedOptions]);

  useEffect(() => {
    setSelectedData(selectedItems);
  }, [selectedItems]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!mounted.current) return;

    setSearchText(e.target.value);
    setSearchQuery(e.target.value);
  };

  const handleCloseRequest = () => {
    onCloseRequest(selectedItems);
  };

  const handleOptionClick = (option: any) => {
    if (!mounted.current) return;

    if (!allowMulti) {
      onChange([option]);
      setSelectedItems([option]);
      setShowDropdown(false);
      onOpen(false);
      return;
    }
    const index = findIndex(selectedItems, { value: option.value });
    if (index < 0) {
      setSelectedItems((prev) => {
        const updatedList = [...prev, option];
        onChange(updatedList);
        return updatedList;
      });
    } else {
      const clone = [...selectedItems];
      clone.splice(index, 1);
      onChange(clone);
      setSelectedItems(clone);
    }
  };

  useOnClickOutside(containerRef, () => {
    if (mounted.current) {
      if (showDropdown) {
        handleCloseRequest();
        setShowDropdown(false);
        onOpen(false);
        setSearchText("");
        setSearchQuery("");
      }
    }
  });

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const renderOption = (option: any) => {
    const isSelected = findIndex(selectedItems, { value: option.value }) >= 0;
    return customOptionComponent ? (
      variant === MULTISELECT_VARIANTS.DUAL_PANE ? (
        <div className="flex justify-between items-center">
          {customOptionComponent(option, handleOptionClick, isSelected)}
          <ChevronRightIcon width="16" height="16" className="text-secondary" />
        </div>
      ) : (
        customOptionComponent(option, handleOptionClick, isSelected)
      )
    ) : (
      <div
        onMouseEnter={() => setHoveredItemValue(option.value)}
        // onMouseLeave={() => setHoveredItemValue(null)}
      >
        <DropdownItem
          option={option}
          onClick={() => handleOptionClick(option)}
          found={isSelected}
          showAvatar={showAvatar}
          maxLabelWidth={maxOptionLabelWidth}
          multiSelect
          variant={variant}
          isHovered={hoveredItemValue === option.value}
          onChildSelect={(child) => handleOptionClick(child)}
          selectedItems={selectedItems}
        />
      </div>
    );
  };

  const renderPreviewItem = (item: any) => {
    return customPreviewItemComponent ? (
      customPreviewItemComponent(item, handleOptionClick)
    ) : (
      <div className="flex items-center justify-between w-full py-md px-0 border-t-0 border-x-0 border-b border-solid border-primary">
        <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="truncate">
          {item.label}
        </BodyText>
        <Button
          variant={BUTTON_VARIANTS.LINK}
          leadingIcon={<MinusCircle className="text-secondary" size={20} />}
          onClick={() => handleOptionClick(item)}
        />
      </div>
    );
  };

  return (
    <>
      <div className="flex w-full items-center justify-between">
        {label && (
          <div className="w-fit">
            <InputLabel
              label={label}
              keyId={keyId}
              uppercaseLabel={uppercaseLabel}
            />
          </div>
        )}
        {infoMessage && (
          <Button
            variant={BUTTON_VARIANTS.LINK}
            leadingIcon={
              <WarningCircle
                className="text-secondary"
                width={20}
                height={20}
              />
            }
            onClick={() => setShowInfoMessage(!showInfoMessage)}
          />
        )}
      </div>
      {showInfoMessage && (
        <BodyText
          size={BODY_TEXT_SIZES.SMALL}
          className="w-full p-md mb-sm font-medium text-primary rounded-lg bg-brand-lightest/50 mt-sm"
        >
          {infoMessage}
        </BodyText>
      )}
      <div
        className={`relative flex items-center justify-between w-full p-md !mt-sm font-manrope cursor-pointer border border-solid rounded-lg ${
          showDropdown
            ? `bg-primary border-brand ${showDropdownClassName}`
            : selectedItems.length > 0
            ? "bg-primary border-primary"
            : "bg-accent border-transparent"
        }`}
        ref={containerRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!mounted.current) return;

          setShowDropdown((prev) => {
            if (prev) {
              handleCloseRequest();
            }
            onOpen(!prev);
            return !prev;
          });
          setSearchText("");
          setSearchQuery("");
        }}
      >
        {effectiveShowChipsInline && selectedItems.length > 0 ? (
          <div className="flex flex-wrap gap-2xs w-full">
            {selectedItems.map((item) =>
              effectiveCustomChipComponent ? (
                <Fragment key={item.value}>
                  {effectiveCustomChipComponent(item, handleOptionClick)}
                </Fragment>
              ) : (
                <BodyText
                  key={item.value}
                  size={BODY_TEXT_SIZES.X_SMALL}
                  className="truncate"
                >
                  {item.label}
                </BodyText>
              ),
            )}
          </div>
        ) : (
          <BodyText
            size={BODY_TEXT_SIZES.X_SMALL}
            className={`${
              showOnlyPlaceHolder || !selectedItems.length
                ? "font-medium text-secondary"
                : "font-bold text-primary"
            }`}
          >
            {selectedItems.length && !showOnlyPlaceHolder
              ? trimText(
                  selectedItems.map((item) => item.label).join(", "),
                  trimCharCount,
                )
              : placeholder}
          </BodyText>
        )}
        <ChevronDownIcon
          width={16}
          height={16}
          className={`cursor-pointer text-secondary ${
            showDropdown ? "rotate-180" : ""
          }`}
        />

        <div
          className={`absolute top-full left-0 z-[1] flex-col ${
            variant === MULTISELECT_VARIANTS.DUAL_PANE ? "w-1/2" : "w-full"
          } max-w-full py-sm px-0 overflow-hidden bg-primary rounded-md shadow-dropdown mt-xs ${
            showDropdown ? "flex" : "hidden"
          } ${showRemoveAllButton ? "max-h-[270px]" : "max-h-[240px]"}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {dropdownTitle && (
            <BodyText
              size={BODY_TEXT_SIZES.X_SMALL}
              color="text-secondary"
              className="px-sm pt-sm pb-sm z-[1]"
            >
              {dropdownTitle}
            </BodyText>
          )}

          {showSearchInput && (
            <Input
              keyId="file-search"
              className="mb-sm pl-3xl w-full"
              wrapperClasses="w-full px-md"
              placeholder={searchPlaceholder || "Search"}
              leadingIconConfig={{
                // @ts-ignore
                icon: <MagnifyingGlass size={16} />,
                className: "top-3",
              }}
              onChange={onSearch}
              value={searchText}
            />
          )}

          <div
            className={`overflow-y-auto ${optionsContainerClassName}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {allowMulti && textAllSelect && (
              <div
                className="flex justify-between items-center py-sm px-md cursor-pointer"
                onClick={() =>
                  setSelectedItems((prev) =>
                    prev.length === options.length ? [] : options,
                  )
                }
              >
                <div className="flex items-center">
                  {showAvatar && (
                    <Avatar className="mr-sm" name={textAllSelect} />
                  )}
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    className={`${maxOptionLabelWidth} truncate`}
                  >
                    {textAllSelect}
                  </BodyText>
                </div>
                {selectedItems.length === options.length && (
                  <span className="flex items-center justify-center ml-sm">
                    <Check width={16} height={16} className="text-brand" />
                  </span>
                )}
              </div>
            )}
            {options.map((option, index) => (
              <Fragment key={index}>
                {renderOption(option)}
                {index === options.length - ITEMS_BEFORE_PAGE ? (
                  <Waypoint onEnter={onScrollToBottom} />
                ) : null}
              </Fragment>
            ))}
            {isLoading ? (
              <BodyText
                size={BODY_TEXT_SIZES.X_SMALL}
                className="px-lg py-sm font-manrope font-medium text-primary"
              >
                {messages?.common?.loading}
              </BodyText>
            ) : null}

            {!options?.length && !isLoading && (
              <BodyText
                size={BODY_TEXT_SIZES.X_SMALL}
                className="px-lg py-sm font-manrope font-medium text-primary"
              >
                {messages?.common?.noOption}
              </BodyText>
            )}
          </div>

          {showRemoveAllButton && selectedItems.length > 0 && (
            <div className="pt-sm pb-2xs px-md border-0 border-t border-solid border-primary">
              <Button
                variant={BUTTON_VARIANTS.LINK}
                text={messages["common"]["filters"]["removeAll"]}
                onClick={() => setSelectedItems([])}
              />
            </div>
          )}
        </div>
      </div>
      {showSelectedOptionsPreviewList &&
        selectedItems.map((option) => (
          <Fragment key={option.value}>{renderPreviewItem(option)}</Fragment>
        ))}
    </>
  );
};

export default AsyncMultiSelectDropdown;
